import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { ILeadMissingFieldDTO } from '@user/store/lead/typings'
import { selectAllBranches } from '@user/store/branches'
import { selectAllCampaigns } from '@user/store/campaigns'
import { selectAllCustomFields } from '@user/store/customFields'
import { selectAllDivisions } from '@user/store/divisions'
import { selectAllLocationTypes } from '@user/store/locationTypes'
import { selectAllSources } from '@user/store/sources'
import type { AutoReviewLookups } from '@user/features/PendingLeadAutoReview/typings'
import { useLoadLookups } from '@user/features/PendingLeadAutoReview/hooks/useLoadLookups'

import { mapMissingFieldsToForm } from '../utils/mapMissingFieldsToForm'

/**
 * Derives what to render, validate and send from the missing fields, and loads the store
 * options the rendered inputs read.
 */
export function useData(missingFields: ILeadMissingFieldDTO[]) {
  const branches = useSelector(selectAllBranches)
  const divisions = useSelector(selectAllDivisions)
  const locationTypes = useSelector(selectAllLocationTypes)
  const campaigns = useSelector(selectAllCampaigns)
  const sources = useSelector(selectAllSources)
  const customFields = useSelector(selectAllCustomFields)

  const lookups = useMemo<AutoReviewLookups>(
    () => ({
      branches: branches.map(({ id, name }) => ({ id, name })),
      divisions: divisions.map(({ id, name }) => ({ id, name })),
      locationTypes: locationTypes.map(({ id, name }) => ({ id, name })),
      campaigns: campaigns.map(({ id, name }) => ({ id: String(id), name })),
      sources: sources.map(({ id, name }) => ({ id, name })),
      customFields,
    }),
    [branches, divisions, locationTypes, campaigns, sources, customFields],
  )

  const model = useMemo(
    () => mapMissingFieldsToForm(missingFields),
    [missingFields],
  )
  const loading = useLoadLookups(model.groups)

  return { ...model, loading, lookups, customFields } as const
}

export type UseData = ReturnType<typeof useData>
